import { QueryClient, keepPreviousData } from '@tanstack/react-query';

/**
 * Configuration du QueryClient pour React Query
 * Optimisé pour éviter les fetchs multiples et gérer le cache efficacement
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 1,
      gcTime: 1000 * 60 * 10,
      retry: 1,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: true,
      refetchInterval: false,
      refetchIntervalInBackground: false,
      placeholderData: keepPreviousData,
    },
    mutations: {
      retry: 1,
      retryDelay: 1000,
    },
  },
});

/**
 * Génère une clé de query pour le fetching des listes (infinite query).
 * Aligned with useModuleData queryKey (no page — pages live inside infinite data).
 */
export const getListQueryKey = ({
  structureId,
  moduleId,
  activeTab = 0,
  searchValue = '',
  orderBy = '',
  order = null,
  numericFilters = [],
  filterFacets = null,
  segmentId = null,
  businessId = null,
  insideBoundingBox = null,
  isDemoMode = false,
}) => {
  return [
    'list-data',
    structureId,
    moduleId,
    segmentId || 'no-segment',
    activeTab,
    businessId,
    orderBy === 'default' ? '' : orderBy || '',
    order,
    JSON.stringify(numericFilters),
    filterFacets ? JSON.stringify(filterFacets) : 'no-facets',
    `search:${searchValue || 'none'}`,
    insideBoundingBox ? JSON.stringify(insideBoundingBox) : 'no-bounds',
    isDemoMode ? 'demo' : 'live',
  ];
};

/**
 * Génère une clé de query pour les données d'élément
 */
export const getElementQueryKey = ({
  structureId,
  elementId,
  businessId = null,
}) => {
  return ['element-data', structureId, elementId, businessId];
};

/**
 * Invalide toutes les queries d'une structure
 */
export const invalidateStructureQueries = (structureId) => {
  return queryClient.invalidateQueries({
    queryKey: ['list-data', structureId],
  });
};

/**
 * Invalide toutes les queries d'un module
 */
export const invalidateModuleQueries = (structureId, moduleId) => {
  return queryClient.invalidateQueries({
    queryKey: ['list-data', structureId, moduleId],
  });
};

/**
 * Préfetch des données pour améliorer la performance
 */
export const prefetchListData = async (queryKey, queryFn) => {
  await queryClient.prefetchQuery({
    queryKey,
    queryFn,
    staleTime: 3000,
  });
};
